/* Progressive enhancement: product and cart forms work through Shopify without JS. */
(() => {
  'use strict';
  const configElement = document.getElementById('ThemeConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const status = document.getElementById('GlobalStatus');
  let mutationQueue = Promise.resolve();
  let cartTrigger = null;
  let drawerRefresh = 0;
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const announce = (message) => { if (status) status.textContent = message; };
  const closeMenu = () => {
    const toggle = document.querySelector('[data-menu-toggle]');
    const nav = document.getElementById('MobileNavigation');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (nav) nav.hidden = true;
  };
  const closeCart = () => { if (drawer()?.open) drawer().close(); };
  const updateCount = (count) => {
    config.cartCount = count;
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
  };
  const cartError = (message) => {
    const error = drawer()?.querySelector('[data-cart-error]');
    if (error) { error.textContent = message; error.hidden = false; }
    announce(message);
  };
  async function fetchDrawerMarkup() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', 'cart-drawer');
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(config.error);
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error(config.error);
    return sections['cart-drawer'];
  }
  function renderCart(markup, count) {
    const target = drawer();
    if (!target) return;
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    if (!content) throw new Error(config.error);
    const active = document.activeElement;
    const activeKey = active?.closest('[data-cart-line-key]')?.dataset.cartLineKey;
    const activeWasInput = active?.matches('input[name="updates[]"]');
    target.querySelector('[data-cart-content]').replaceWith(content);
    if (typeof count === 'number') updateCount(count);
    if (target.open) {
      const row = [...target.querySelectorAll('[data-cart-line-key]')].find((item) => item.dataset.cartLineKey === activeKey);
      const nextFocus = activeWasInput && row ? row.querySelector('input') : target.querySelector('[data-cart-close]');
      nextFocus?.focus({ preventScroll: true });
    }
  }
  async function openCart(trigger, refresh = true) {
    const target = drawer();
    if (!target || typeof target.showModal !== 'function') { window.location.assign(config.cart); return; }
    if (!target.open) {
      cartTrigger = trigger || document.activeElement;
      closeMenu();
      target.showModal();
      document.body.classList.add('modal-open');
    }
    if (!refresh) return;
    const refreshId = ++drawerRefresh;
    target.setAttribute('aria-busy', 'true');
    try {
      const [markup, cartResponse] = await Promise.all([
        fetchDrawerMarkup(), fetch(`${config.cart}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      ]);
      if (!cartResponse.ok) throw new Error(config.error);
      const cart = await cartResponse.json();
      if (refreshId === drawerRefresh) renderCart(markup, cart.item_count);
    } catch (error) { cartError(error.message || config.error); }
    finally { if (refreshId === drawerRefresh) target.removeAttribute('aria-busy'); }
  }
  function enqueueMutation(task) {
    ++drawerRefresh; // Any earlier read response must not overwrite a mutation.
    const operation = mutationQueue.then(task);
    mutationQueue = operation.catch(() => {});
    return operation;
  }
  async function changeCart(key, quantity) {
    const target = drawer();
    target?.setAttribute('aria-busy', 'true');
    try {
      await enqueueMutation(async () => {
        const response = await fetch(`${config.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        const cart = await response.json();
        if (!response.ok || cart.status >= 400) throw new Error(cart.description || cart.message || config.error);
        const markup = cart.sections?.['cart-drawer'] || await fetchDrawerMarkup();
        renderCart(markup, cart.item_count);
        announce(`${cart.item_count} ${document.querySelector('.cart-link .visually-hidden')?.textContent || 'items'}`);
      });
    } catch (error) {
      // Reset an optimistic quantity after a Shopify stock or quantity-rule error.
      try { renderCart(await fetchDrawerMarkup()); } catch (_) { /* Keep the native cart link available. */ }
      cartError(error.message || config.error);
    } finally { target?.removeAttribute('aria-busy'); }
  }
  function updateVariant(root) {
    const input = root.querySelector('[data-variant-select]');
    const data = root.querySelector('[data-variants]');
    if (!input || !data) return;
    const variant = JSON.parse(data.textContent).find((item) => String(item.id) === input.value);
    const available = Boolean(variant?.available);
    root.querySelectorAll('[data-add-button]').forEach((button) => { button.disabled = !available; });
    root.querySelectorAll('[data-add-label]').forEach((label) => { label.textContent = variant ? (available ? config.add : config.soldOut) : config.unavailable; });
    root.querySelectorAll('[data-current-price], [data-sticky-price]').forEach((node) => { node.textContent = variant?.price || ''; });
    const compare = root.querySelector('[data-compare-price]');
    if (compare) { compare.textContent = variant?.compare || ''; compare.hidden = !variant?.compare; }
    const unit = root.querySelector('[data-unit-price]');
    if (unit) { unit.textContent = variant?.unitPrice || ''; unit.hidden = !variant?.unitPrice; }
    const inventory = root.querySelector('[data-inventory]');
    if (inventory) inventory.textContent = variant?.inventory || '';
    const checkout = root.querySelector('[data-dynamic-checkout]');
    if (checkout) checkout.hidden = !available;
    const quantity = root.querySelector('input[name="quantity"]');
    if (quantity && variant) {
      quantity.min = variant.min;
      quantity.step = variant.step;
      if (variant.max !== null) quantity.max = variant.max; else quantity.removeAttribute('max');
      quantity.value = variant.min;
    }
    const error = root.querySelector('[data-form-error]');
    if (error) error.hidden = true;
    if (variant?.mediaId) showMedia(root, String(variant.mediaId));
    if (root.hasAttribute('data-product-page') && variant) {
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }
  function showMedia(root, mediaId) {
    if (!root.querySelector(`[data-media-id="${mediaId}"]`)) return;
    root.querySelectorAll('[data-media-id]').forEach((panel) => {
      const selected = panel.dataset.mediaId === mediaId;
      panel.hidden = !selected;
      if (!selected) {
        panel.querySelectorAll('video').forEach((video) => video.pause());
        // Unload external embeds when leaving a media item.
        const iframe = panel.querySelector('iframe');
        if (iframe) { iframe.remove(); const play = panel.querySelector('[data-load-video]'); if (play) play.hidden = false; }
      }
    });
    root.querySelectorAll('[data-media-target]').forEach((button) => { button.setAttribute('aria-current', String(button.dataset.mediaTarget === mediaId)); });
  }
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-menu-toggle]');
    if (toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      document.getElementById('MobileNavigation').hidden = expanded;
    }
    if (event.target.closest('#MobileNavigation a')) closeMenu();
    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) { event.preventDefault(); openCart(cartOpen); }
    if (event.target.closest('[data-cart-close]')) closeCart();
    const quantityButton = event.target.closest('[data-quantity]');
    if (quantityButton) {
      const input = quantityButton.closest('.quantity').querySelector('input');
      const delta = Number(quantityButton.dataset.quantity) * Number(input.step || 1);
      const minimum = Number(input.min || 0);
      const maximum = input.max !== '' ? Number(input.max) : Infinity;
      const next = Math.min(maximum, Math.max(minimum, (Number(input.value) || minimum) + delta));
      if (Number(input.value) !== next) { input.value = next; input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeCart(remove.closest('[data-cart-line-key]').dataset.cartLineKey, 0); }
    const media = event.target.closest('[data-media-target]');
    if (media) showMedia(media.closest('[data-gallery]'), media.dataset.mediaTarget);
    const play = event.target.closest('[data-load-video]');
    if (play) { play.parentElement.appendChild(play.parentElement.querySelector('template').content.cloneNode(true)); play.hidden = true; }
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-select]')) updateVariant(event.target.closest('[data-product-root]'));
    if (event.target.matches('[data-cart-auto] input[name="updates[]"]')) {
      if (!event.target.checkValidity()) { event.target.reportValidity(); return; }
      changeCart(event.target.closest('[data-cart-line-key]').dataset.cartLineKey, Number(event.target.value));
    }
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch || !drawer()?.showModal) return;
    event.preventDefault();
    if (form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';
    const root = form.closest('[data-product-root]');
    const buttons = root.querySelectorAll('[data-add-button]');
    buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    const error = root.querySelector('[data-form-error]');
    if (error) error.hidden = true;
    try {
      await enqueueMutation(async () => {
        const data = new FormData(form);
        data.append('sections', 'cart-drawer'); data.append('sections_url', window.location.pathname);
        const response = await fetch(`${config.cartAdd}.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || config.error);
        // An add succeeded already: never retry it if section rendering fails.
        try {
          const markup = result.sections?.['cart-drawer'] || await fetchDrawerMarkup();
          renderCart(markup);
          const cartResponse = await fetch(`${config.cart}.js`, { cache: 'no-store' });
          if (cartResponse.ok) updateCount((await cartResponse.json()).item_count);
          await openCart(event.submitter || root.querySelector('[data-add-button]'), false);
          announce(config.added);
        } catch (_) { window.location.assign(config.cart); }
      });
    } catch (failure) {
      if (error) { error.textContent = failure.message || config.error; error.hidden = false; }
      announce(failure.message || config.error);
    } finally {
      form.dataset.submitting = 'false';
      buttons.forEach((button) => { button.removeAttribute('aria-busy'); });
      // Preserve the entered quantity while restoring availability.
      const variantData = JSON.parse(root.querySelector('[data-variants]').textContent);
      const variant = variantData.find((item) => String(item.id) === form.querySelector('[name="id"]').value);
      buttons.forEach((button) => { button.disabled = !variant?.available; });
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('[data-menu-toggle][aria-expanded="true"]')) {
      closeMenu(); document.querySelector('[data-menu-toggle]')?.focus();
    }
  });
  function initialize(context = document) {
    if (context.querySelector('[data-product-sticky]')) document.body.classList.add('has-product-sticky');
    const target = context.querySelector('[data-cart-drawer]');
    if (target && !target.dataset.initialized) {
      target.dataset.initialized = 'true';
      target.addEventListener('close', () => { document.body.classList.remove('modal-open'); if (cartTrigger?.isConnected) cartTrigger.focus({ preventScroll: true }); });
      target.addEventListener('click', (event) => {
        if (event.target !== target) return;
        const rect = target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeCart();
      });
    }
  }
  initialize();
  window.matchMedia('(min-width: 750px)').addEventListener('change', (event) => { if (event.matches) closeMenu(); });
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    document.body.classList.toggle('has-product-sticky', Boolean(document.querySelector('[data-product-sticky]')));
  });
  document.addEventListener('shopify:section:unload', () => {
    queueMicrotask(() => { document.body.classList.toggle('has-product-sticky', Boolean(document.querySelector('[data-product-sticky]'))); });
  });
  document.addEventListener('shopify:block:select', (event) => { const details = event.target.closest('details') || event.target.querySelector('details'); if (details) details.open = true; });
})();
